import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { ISODateString, PeriodMonth, Person, TimeEntry } from '../types/timesheet.types';
import { TimesheetStatus, ProjectType, ProjectStatus } from '../types/timesheet.types';
import { useTimesheetState } from '../hooks/useTimesheetState';
import { useWeekNavigation } from '../hooks/useWeekNavigation';
import { useConnector } from '../contexts/ConnectorContext';
import {
  getWeekDays, isDateInMonth, isWeekend, parseISODate,
  addMonths, addWeeks, parsePeriodMonth, formatMonthLabel,
  getCalendarWeeksForMonth, formatWeekLabel, getWorkingDaysInMonth,
} from '../utils/dateUtils';
import { VALIDATION, BANK_HOLIDAY_PROJECT_CODE } from '../utils/constants';
import { validateMonthSubmission } from '../utils/validation';
import { hhmmToDecimal, decimalToHHMM, fmtHours } from '../utils/calculations';
import { buildGridRows, type GridRow, type CellData } from '../utils/timesheetTransform';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Props {
  userEmail: string;
  userName: string;
  currentPerson: Person;
  /** Viewer is a line manager / PMO / admin — may view direct reports. */
  canActAsManager?: boolean;
  /** Viewer is a super approver — may view everyone. */
  canActAsSuperApprover?: boolean;
  /** Viewer is in the Unlock Timesheet Permitted Group — may view and edit everyone's timesheet regardless of status. */
  canActAsUnlockGroup?: boolean;
}

// ── Status helpers ────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<TimesheetStatus, string> = {
  [TimesheetStatus.Draft]:           'Draft',
  [TimesheetStatus.Submitted]:       'Submitted',
  [TimesheetStatus.ManagerApproved]: 'Approved',
  [TimesheetStatus.SuperApproved]:   'Approved',
  [TimesheetStatus.Locked]:          'Locked',
  [TimesheetStatus.Rejected]:        'Rejected',
  [TimesheetStatus.ReadOnly]:        'Read-only',
};

const PILL_CLASS: Record<TimesheetStatus, string> = {
  [TimesheetStatus.Draft]:           'pill pill-draft',
  [TimesheetStatus.Submitted]:       'pill pill-submitted',
  [TimesheetStatus.ManagerApproved]: 'pill pill-approved',
  [TimesheetStatus.SuperApproved]:   'pill pill-approved',
  [TimesheetStatus.Locked]:          'pill pill-locked',
  [TimesheetStatus.Rejected]:        'pill pill-rejected',
  [TimesheetStatus.ReadOnly]:        'pill pill-locked',
};

function effectiveStatus(entries: { status: TimesheetStatus }[]): TimesheetStatus {
  if (!entries.length) return TimesheetStatus.Draft;
  const s = entries.map(e => e.status);
  if (s.includes(TimesheetStatus.Locked))          return TimesheetStatus.Locked;
  if (s.includes(TimesheetStatus.SuperApproved))   return TimesheetStatus.SuperApproved;
  if (s.includes(TimesheetStatus.ManagerApproved)) return TimesheetStatus.ManagerApproved;
  if (s.includes(TimesheetStatus.Rejected))        return TimesheetStatus.Rejected;
  if (s.every(x => x === TimesheetStatus.Submitted)) return TimesheetStatus.Submitted;
  return TimesheetStatus.Draft;
}

// ── Badge helper ──────────────────────────────────────────────────────────────

const TYPE_BADGE: Record<ProjectType, { cls: string; label: string }> = {
  [ProjectType.RD]:         { cls: 'badge-rd',         label: 'R&D' },
  [ProjectType.GA]:         { cls: 'badge-ga',          label: 'G&A' },
  [ProjectType.Leave]:      { cls: 'badge-leave',       label: 'Leave' },
  [ProjectType.Commercial]: { cls: 'badge-commercial',  label: 'Commercial' },
  [ProjectType.Standard]:   { cls: 'badge-standard',    label: 'Standard' },
};

function TypeBadge({ type }: { type: ProjectType | null }) {
  if (!type) return null;
  const { cls, label } = TYPE_BADGE[type];
  return <span className={`badge ${cls}`}>{label}</span>;
}

// ── Day header labels ─────────────────────────────────────────────────────────

const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function formatDayShort(date: ISODateString): string {
  const d = parseISODate(date);
  return `${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`;
}

function formatShortMonth(pm: PeriodMonth): string {
  const { year, month } = parsePeriodMonth(pm);
  return new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'short' });
}

// ── Main component ────────────────────────────────────────────────────────────

export function MyTimesheet({
  userEmail,
  userName,
  currentPerson,
  canActAsManager = false,
  canActAsSuperApprover = false,
  canActAsUnlockGroup = false,
}: Props) {
  const connector = useConnector();

  const { weekStart, selectedMonth: periodMonth, calendarMonth, weekLabel, goToPrevWeek, goToNextWeek, goToPrevMonth, goToNextMonth, goToWeek } =
    useWeekNavigation();

  // ── Person selector (managers see direct reports, super approvers see all) ──

  const [viewedPerson, setViewedPerson] = useState<Person>(currentPerson);
  const [peopleList, setPeopleList] = useState<Person[]>([]);

  useEffect(() => {
    if (!canActAsManager && !canActAsSuperApprover && !canActAsUnlockGroup) return;
    let cancelled = false;
    let load: Promise<Person[]>;
    if (canActAsUnlockGroup) {
      load = connector.getPeople({ active: true });
    } else if (canActAsSuperApprover) {
      load = connector.getSuperApproverReports(currentPerson.m365Email);
    } else {
      load = Promise.all([
        connector.getDirectReports(currentPerson.m365Email),
        connector.getBackupReports(currentPerson.m365Email),
      ]).then(([directs, backups]) => [...directs, ...backups]);
    }
    load
      .then(people => {
        if (cancelled) return;
        setPeopleList(people.filter(p => p.id !== currentPerson.id));
      })
      .catch(() => { if (!cancelled) setPeopleList([]); });
    return () => { cancelled = true; };
  }, [connector, canActAsManager, canActAsSuperApprover, canActAsUnlockGroup, currentPerson.id, currentPerson.m365Email]);

  const isOwnSheet = viewedPerson.id === currentPerson.id;
  const activePersonId   = viewedPerson.id;
  const activeUserEmail  = isOwnSheet ? userEmail : viewedPerson.m365Email;
  const activeUserName   = isOwnSheet ? userName  : viewedPerson.fullName;

  const {
    entries, projects, bankHolidays, bankHolidayDates, dailyTotals, monthlyTotal,
    isDirty, isSaving, saveError, lastSavedAt,
    upsertCellHours, save, submitMonth, discardChanges, removeProjectEntries,
    isLoading, loadError, isDayOverThreshold, isDayBankHoliday,
  } = useTimesheetState(
    activeUserEmail,
    activeUserName,
    periodMonth,
    viewedPerson.lineManager,
    viewedPerson.lineManagerEmail,
    viewedPerson.superApprover,
    viewedPerson.superApproverEmail,
    canActAsUnlockGroup,
    userName,
    userEmail,
  );

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);

  const monthWeeks = useMemo(() => getCalendarWeeksForMonth(periodMonth), [periodMonth]);
  const isFirstWeekOfMonth = monthWeeks.length > 0 && weekStart === monthWeeks[0].weekStart;
  const isLastWeekOfMonth  = monthWeeks.length > 0 && weekStart === monthWeeks[monthWeeks.length - 1].weekStart;

  // ── Previous month total (for delta display) ──────────────────────────────

  const [prevMonthTotal, setPrevMonthTotal] = useState<number | null>(null);
  useEffect(() => {
    const prevMonth = addMonths(periodMonth, -1);
    let cancelled = false;
    connector.getTimeEntries({ personEmail: activeUserEmail, periodMonth: prevMonth })
      .then(es => { if (!cancelled) setPrevMonthTotal(es.reduce((s, e) => s + e.hours, 0)); })
      .catch(() => { if (!cancelled) setPrevMonthTotal(null); });
    return () => { cancelled = true; };
  }, [connector, activePersonId, periodMonth]);

  // ── Year-to-date total ──────────────────────────────────────────────────────

  const [ytdTotal, setYtdTotal] = useState<number | null>(null);
  useEffect(() => {
    const { year } = parsePeriodMonth(periodMonth);
    const yearStr = String(year);
    let cancelled = false;
    connector.getTimeEntries({
      personEmail: activeUserEmail,
      dateFrom: `${yearStr}-01-01` as ISODateString,
      dateTo: `${yearStr}-12-31` as ISODateString,
    })
      .then(es => { if (!cancelled) setYtdTotal(es.reduce((s, e) => s + e.hours, 0)); })
      .catch(() => { if (!cancelled) setYtdTotal(null); });
    return () => { cancelled = true; };
  }, [connector, activePersonId, periodMonth]);

  // ── User-added project rows ('' = pending selection) ──────────────────────
  // Includes rows pre-loaded from the previous week (rolling week / same-as-last-week).

  const [addedCodes, setAddedCodes] = useState<string[]>([]);
  // Tracks which weekStart values we've already attempted roll-over for, so
  // navigating back to a week doesn't re-roll after the user has removed codes.
  const rolledOverWeeks = useRef<Set<ISODateString>>(new Set());
  // Per-month map of confirmed project codes; keyed by calendarMonth.
  // Persists codes within a month across week navigation; independent per month.
  const monthAddedCodes = useRef<Map<string, string[]>>(new Map());

  useEffect(() => {
    // Person change: clear all per-month code history.
    monthAddedCodes.current = new Map();
  }, [activePersonId]);

  useEffect(() => {
    // Month or person change: restore this month's codes and reset roll-over tracking.
    rolledOverWeeks.current = new Set();
    setAddedCodes(monthAddedCodes.current.get(calendarMonth) ?? []);
  }, [calendarMonth, activePersonId]);

  // Roll over previous week's project codes when the current week is empty
  // and this month has no user-added codes yet.
  useEffect(() => {
    if (isLoading) return;
    if (rolledOverWeeks.current.has(weekStart)) return;

    const hasCurrentWeekEntries = entries.some(
      e => (weekDays as readonly string[]).includes(e.dateWorked)
        && e.projectCode !== BANK_HOLIDAY_PROJECT_CODE
        && e.hours > 0,
    );
    if (hasCurrentWeekEntries) {
      rolledOverWeeks.current.add(weekStart);
      return;
    }

    // Skip roll-over if user already has codes for this month.
    if ((monthAddedCodes.current.get(calendarMonth) ?? []).length > 0) {
      rolledOverWeeks.current.add(weekStart);
      return;
    }

    let cancelled = false;
    const prevWeekDays = getWeekDays(addWeeks(weekStart, -1));
    connector.getTimeEntries({
      personEmail: activeUserEmail,
      dateFrom: prevWeekDays[0],
      dateTo: prevWeekDays[6],
    })
      .then(prev => {
        if (cancelled) return;
        const codes = [...new Set(
          prev
            .filter(e => e.projectCode && e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.hours > 0)
            .map(e => e.projectCode),
        )];
        rolledOverWeeks.current.add(weekStart);
        setAddedCodes(prev => {
          const pending = prev.filter(c => !c);
          const merged = [...new Set([...pending, ...codes])];
          monthAddedCodes.current.set(calendarMonth, merged.filter(Boolean));
          return merged;
        });
      })
      .catch(() => { rolledOverWeeks.current.add(weekStart); });

    return () => { cancelled = true; };
  }, [connector, activePersonId, weekStart, calendarMonth, isLoading, entries, weekDays]);

  // ── Bank holiday overrides (dates the user has indicated they actually worked) ──

  const [overriddenBHDates, setOverriddenBHDates] = useState<Set<ISODateString>>(new Set());

  useEffect(() => {
    setOverriddenBHDates(new Set());
  }, [calendarMonth, activePersonId]);


  const handleToggleBHOverride = useCallback((date: ISODateString) => {
    // If project hours already exist for this BH day, require confirmation before deleting
    const hasWorkedHours = entries.some(
      e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.dateWorked === date && e.hours > 0
    );
    if (hasWorkedHours) {
      setPendingBHRestoreDate(date);
      return;
    }
    setOverriddenBHDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }, [entries]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showCancelConfirm, setShowCancelConfirm]       = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm]       = useState(false);
  const [pendingRemoveCode, setPendingRemoveCode]       = useState<string | null>(null);
  const [pendingBHRestoreDate, setPendingBHRestoreDate] = useState<ISODateString | null>(null);
  const [showWeekPicker, setShowWeekPicker]         = useState(false);
  const [submitErrors, setSubmitErrors]             = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting]             = useState(false);
  const [showSaving, setShowSaving]                 = useState(false);

  // ── Derived: week entries + status ────────────────────────────────────────

  const weekEntries = useMemo(
    () => entries.filter(e => (weekDays as readonly string[]).includes(e.dateWorked)),
    [entries, weekDays],
  );

  // Week-level status drives the status card / pill display.
  const currentStatus = useMemo(() => effectiveStatus(weekEntries), [weekEntries]);

  // Month-level status drives whether the grid and action bar are read-only.
  // Submission is always month-scoped, so a submitted/approved month locks all weeks.
  const monthStatus = useMemo(() => effectiveStatus(entries as { status: TimesheetStatus }[]), [entries]);

  const isReadOnlySheet =
    canActAsUnlockGroup
      ? false
      : (!isOwnSheet ||
          ((monthStatus === TimesheetStatus.Submitted ||
            monthStatus === TimesheetStatus.ManagerApproved ||
            monthStatus === TimesheetStatus.SuperApproved ||
            monthStatus === TimesheetStatus.Locked ||
            monthStatus === TimesheetStatus.ReadOnly) && !canActAsSuperApprover));

  // ── Grid rows ─────────────────────────────────────────────────────────────

  const confirmedCodes = useMemo(() => addedCodes.filter(Boolean), [addedCodes]);

  const { rows: unsortedRows, bhRow } = useMemo(
    () => buildGridRows(entries, projects, bankHolidays, weekDays, periodMonth, confirmedCodes, isReadOnlySheet, overriddenBHDates),
    [entries, projects, bankHolidays, weekDays, periodMonth, confirmedCodes, isReadOnlySheet, overriddenBHDates],
  );

  // Sort rows deterministically so entering hours (which mutates entries and
  // rebuilds allCodes inside buildGridRows) never changes a row's position.
  const rows = useMemo(() => {
    const isDPCode = (code: string) => /^[DP]/i.test(code);
    const isBottom = (t: ProjectType | null) => t === ProjectType.Leave || t === ProjectType.GA;
    return [...unsortedRows].sort((a, b) => {
      const tier = (r: typeof a) => isBottom(r.projectType) ? 2 : isDPCode(r.projectCode) ? 0 : 1;
      const tA = tier(a), tB = tier(b);
      if (tA !== tB) return tA - tB;
      return a.projectCode.localeCompare(b.projectCode);
    });
  }, [unsortedRows]);

  // ── Weekly grand total (project rows + BH row) ────────────────────────────

  const grandTotal = useMemo(
    () => rows.reduce((s, r) => s + r.rowTotal, 0) + (bhRow?.rowTotal ?? 0),
    [rows, bhRow],
  );

  // ── Summary figures ───────────────────────────────────────────────────────

  const rdHoursWeek = useMemo(() => {
    const rdCodes = new Set(projects.filter(p => p.type === ProjectType.RD).map(p => p.projectCode));
    return weekEntries.filter(e => rdCodes.has(e.projectCode)).reduce((s, e) => s + e.hours, 0);
  }, [weekEntries, projects]);

  const rdMonthlyTotal = useMemo(() => {
    const rdCodes = new Set(projects.filter(p => p.type === ProjectType.RD).map(p => p.projectCode));
    return entries.filter(e => rdCodes.has(e.projectCode)).reduce((s, e) => s + e.hours, 0);
  }, [entries, projects]);

  const gaHoursWeek = useMemo(() => {
    const gaCodes = new Set(projects.filter(p => p.type === ProjectType.GA).map(p => p.projectCode));
    return weekEntries.filter(e => gaCodes.has(e.projectCode)).reduce((s, e) => s + e.hours, 0);
  }, [weekEntries, projects]);

  const bhHoursWeek = useMemo(
    () => bankHolidays
      .filter(bh => (weekDays as readonly string[]).includes(bh.holidayDate) && !overriddenBHDates.has(bh.holidayDate))
      .reduce((s, bh) => s + bh.defaultHours, 0),
    [bankHolidays, weekDays, overriddenBHDates],
  );

  // ── Submit label ──────────────────────────────────────────────────────────

  const submitLabel = isSubmitting ? 'Submitting…' : isLastWeekOfMonth ? 'Submit for Month' : 'Submit for Approval';

  const monthDisplayLabel = (() => {
    const [y, m] = periodMonth.split('-');
    return `${new Date(+y, +m - 1, 1).toLocaleString('en-GB', { month: 'long' })} ${y}`;
  })();

  // ── Available projects (not yet in grid) ──────────────────────────────────

  const existingCodes = useMemo(
    () => new Set([...rows.map(r => r.projectCode), ...confirmedCodes]),
    [rows, confirmedCodes],
  );
  const availableProjects = useMemo(
    () => projects
      .filter(p => {
        if (existingCodes.has(p.projectCode)) return false;
        return weekDays.some(d =>
          d >= p.startDate && (!p.endDate || d <= p.endDate),
        );
      })
      .sort((a, b) => {
        const isBottom = (t: ProjectType) => t === ProjectType.Leave || t === ProjectType.GA;
        const isDPCode = (code: string) => /^[DP]/i.test(code);
        const tier = (p: typeof a) => isBottom(p.type) ? 2 : isDPCode(p.projectCode) ? 0 : 1;
        const tA = tier(a), tB = tier(b);
        if (tA !== tB) return tA - tB;
        return a.projectCode.localeCompare(b.projectCode);
      }),
    [projects, existingCodes, weekDays],
  );

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCellChange = useCallback(
    (projectCode: string, date: ISODateString, value: string) => {
      if (value === '') { upsertCellHours(projectCode, date, null); return; }
      const decimal = hhmmToDecimal(value);
      if (!isNaN(decimal) && decimal >= 0 && decimal <= VALIDATION.MAX_DAILY_HOURS) {
        upsertCellHours(projectCode, date, decimal);
      }
    },
    [upsertCellHours],
  );

  const handleRemoveRow = useCallback((projectCode: string) => {
    setPendingRemoveCode(projectCode);
  }, []);

  const confirmRemoveRow = useCallback((projectCode: string) => {
    const idsToDelete = entries
      .filter(e => e.projectCode === projectCode && !e.id.startsWith('new-'))
      .map(e => e.id);

    // Immediately remove from local state so the row disappears and won't come back on save.
    removeProjectEntries(projectCode);

    setAddedCodes(prev => {
      const next = prev.filter(c => c !== projectCode);
      monthAddedCodes.current.set(calendarMonth, next.filter(Boolean));
      return next;
    });
    setPendingRemoveCode(null);

    if (idsToDelete.length > 0) {
      void connector.deleteTimeEntries(idsToDelete);
    }
  }, [calendarMonth, entries, connector, removeProjectEntries]);

  const confirmBHRestore = useCallback((date: ISODateString) => {
    // Delete all project hours logged for this BH date
    const workedEntries = entries.filter(
      e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.dateWorked === date && e.hours > 0
    );
    workedEntries.forEach(e => upsertCellHours(e.projectCode, date, null));
    // Unmark as overridden so the cell returns to read-only BH state
    setOverriddenBHDates(prev => {
      const next = new Set(prev);
      next.delete(date);
      return next;
    });
    setPendingBHRestoreDate(null);
  }, [entries, upsertCellHours]);

  const handleSaveDraft = useCallback(async () => {
    setShowSaving(true);
    try { await save(); } finally {
      setTimeout(() => setShowSaving(false), 1500);
    }
  }, [save]);

  // Always-fresh save ref: updated during render so effects see the latest save
  const saveRef = useRef(save);
  saveRef.current = save;

  // Auto-save every minute when there are unsaved changes in Draft or Rejected status.
  // Only active while this component is mounted (user is on this screen).
  useEffect(() => {
    const timer = setInterval(() => {
      if (
        !isDirty || isSaving ||
        (monthStatus !== TimesheetStatus.Draft && monthStatus !== TimesheetStatus.Rejected)
      ) return;
      void saveRef.current();
    }, 60_000);
    return () => clearInterval(timer);
  }, [isDirty, isSaving, monthStatus]);

  // STEP 1+2: validate then save, then show confirmation modal.
  const handleSubmitRequest = useCallback(async () => {
    setSubmitErrors([]);
    const allWorkingDays = getWorkingDaysInMonth(periodMonth);
    const validation = validateMonthSubmission(
      entries as TimeEntry[],
      periodMonth,
      bankHolidayDates,
      allWorkingDays,
    );
    if (!validation.valid) {
      setSubmitErrors(validation.errors);
      return;
    }
    if (isDirty) {
      try {
        await save();
      } catch {
        setSubmitErrors(['Save failed — please fix errors before submitting']);
        return;
      }
    }
    setShowSubmitConfirm(true);
  }, [entries, periodMonth, bankHolidayDates, isDirty, save]);

  // STEP 3+4: called after user confirms in modal — submit all month entries.
  const handleSubmitConfirm = useCallback(async () => {
    setShowSubmitConfirm(false);
    setIsSubmitting(true);
    setSubmitErrors([]);
    try {
      const errors = await submitMonth(activePersonId, activeUserName, 'MyTimesheet');
      if (errors.length > 0) setSubmitErrors(errors);
    } catch (err) {
      setSubmitErrors([err instanceof Error ? err.message : 'Submission failed — please try again']);
    } finally {
      setIsSubmitting(false);
    }
  }, [submitMonth, activePersonId, activeUserName]);
/*
  const handleCancel = useCallback(() => {
    if (isDirty) setShowCancelConfirm(true);
    else { discardChanges(); setAddedCodes(prev => prev.filter(Boolean)); }
  }, [isDirty, discardChanges]);
*/
  const handleExport = useCallback(() => {
    const { year, month } = parsePeriodMonth(periodMonth);
    const daysInMonth = new Date(year, month, 0).getDate();
    const days: string[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      days.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }

    // Build map: projectCode -> { projectName, hours by date }
    const projectMap = new Map<string, { name: string; byDate: Map<string, number> }>();
    for (const entry of entries) {
      if (!days.includes(entry.dateWorked)) continue;
      if (!projectMap.has(entry.projectCode)) {
        const proj = projects.find(p => p.projectCode === entry.projectCode);
        projectMap.set(entry.projectCode, { name: proj?.projectName ?? entry.projectCode, byDate: new Map() });
      }
      projectMap.get(entry.projectCode)!.byDate.set(entry.dateWorked, entry.hours);
    }

    const dayHeaders = days.map(d => {
      const dt = parseISODate(d as ISODateString);
      return `${dt.getDate()} ${dt.toLocaleDateString('en-GB', { month: 'short' })}`;
    });

    const header = ['Project Code', 'Project Name', ...dayHeaders, 'Total'];
    const csvRows = [header];

    for (const [code, { name, byDate }] of projectMap) {
      const hourCells = days.map(d => byDate.get(d) ?? 0);
      const total = hourCells.reduce((s, h) => s + h, 0);
      csvRows.push([code, name, ...hourCells.map(h => h > 0 ? String(h) : ''), String(total)]);
    }

    const csv = csvRows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet-${activeUserName.replace(/\s+/g, '-')}-${periodMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [entries, projects, periodMonth, activeUserName]);

  // ── Saved label ───────────────────────────────────────────────────────────

  const savedLabel = useMemo(() => {
    if (!lastSavedAt) return '';
    const hh = String(lastSavedAt.getHours()).padStart(2, '0');
    const mm = String(lastSavedAt.getMinutes()).padStart(2, '0');
    return `Saved ${hh}:${mm}`;
  }, [lastSavedAt]);

  // ── Month labels ──────────────────────────────────────────────────────────

  const prevMonth = addMonths(periodMonth, -1);
  const monthLabel = formatMonthLabel(periodMonth);
  const prevMonthShortLabel = formatShortMonth(prevMonth);
  const monthShortLabel = formatShortMonth(periodMonth);

  // ── Rejection note (first non-empty note across all entries) ─────────────

  const rejectionNote = useMemo(
    () => entries.find(e => e.rejectionNote)?.rejectionNote ?? '',
    [entries],
  );

  // ── Status card styles ────────────────────────────────────────────────────

  const statusCardCls = currentStatus === TimesheetStatus.Draft ? 'sum-card c-amber'
    : currentStatus === TimesheetStatus.ManagerApproved || currentStatus === TimesheetStatus.SuperApproved ? 'sum-card c-green'
    : currentStatus === TimesheetStatus.Rejected ? 'sum-card c-red'
    : 'sum-card';

  // ── Early returns ─────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" aria-label="Loading timesheet" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="content">
        <div className="err-banner">
          <strong>Could not load timesheet.</strong>&nbsp;{loadError.message}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Timesheets</span>
            <span className="sep">/</span>
            <span className="cur">My Timesheet</span>
          </div>
          <div className="wk-nav" style={{ position: 'relative' }}>
            <button onClick={goToPrevMonth} title="Previous month">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span
              className="wk-lbl-top wk-lbl-click"
              onClick={() => setShowWeekPicker(v => !v)}
              title="Click to select a month"
            >
              {monthLabel}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <button onClick={goToNextMonth} title="Next month">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            {showWeekPicker && (
              <div
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => setShowWeekPicker(false)}
              >
                <div
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, width: 300, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>Jump to Week</span>
                    <button onClick={() => setShowWeekPicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 18, lineHeight: 1 }}>✕</button>
                  </div>
                  {getCalendarWeeksForMonth(periodMonth).slice().reverse().map(w => (
                    <div
                      key={w.weekStart}
                      onClick={() => { goToWeek(w.weekStart); setShowWeekPicker(false); }}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 13.5,
                        marginBottom: 4,
                        border: '1px solid var(--border)',
                        background: w.weekStart === weekStart ? 'var(--accent)' : '',
                        color: w.weekStart === weekStart ? '#fff' : 'var(--text)',
                        transition: 'background 0.15s',
                      }}
                      onMouseOver={e => { if (w.weekStart !== weekStart) (e.currentTarget as HTMLDivElement).style.background = 'var(--surface2)'; }}
                      onMouseOut={e => { if (w.weekStart !== weekStart) (e.currentTarget as HTMLDivElement).style.background = ''; }}
                    >
                      {formatWeekLabel(w.weekStart)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="topbar-r">
          {(canActAsManager || canActAsSuperApprover || canActAsUnlockGroup) && peopleList.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Viewing:</span>
              <select
                className="finput"
                style={{ maxWidth: 260, fontSize: 13 }}
                value={viewedPerson.id}
                onChange={e => {
                  const selectedId = e.target.value;
                  if (selectedId === currentPerson.id) {
                    setViewedPerson(currentPerson);
                    return;
                  }
                  const p = peopleList.find(x => x.id === selectedId);
                  if (p) setViewedPerson(p);
                }}
                title={canActAsUnlockGroup
                  ? 'Pick any person to view and edit their timesheet'
                  : canActAsSuperApprover
                    ? 'Pick any person to view their timesheet (read-only)'
                    : 'Pick a direct report to view their timesheet (read-only)'}
              >
                <option value={currentPerson.id}>My Timesheet</option>
                {peopleList.map(p => (
                  <option key={p.id} value={p.id}>{p.fullName}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="content">

        {/* Summary cards */}
        <div className="sum-grid">
          <div className="sum-card c-accent">
            <div className="sum-lbl">Hours This Week</div>
            <div className="sum-val">{fmtHours(grandTotal)}</div>
            <div className="sum-sub">of 37.5h target</div>
          </div>
          <div className="sum-card">
            <div className="sum-lbl">Hours This Month</div>
            <div className="sum-val">{fmtHours(monthlyTotal)}</div>
            <div className="sum-sub">
              {prevMonthTotal !== null && prevMonthTotal > 0 ? (
                <>
                  <span className={`sum-trend ${monthlyTotal >= prevMonthTotal ? 'up' : 'down'}`}>
                    {monthlyTotal >= prevMonthTotal ? '▲' : '▼'} {fmtHours(Math.abs(monthlyTotal - prevMonthTotal))}
                  </span>
                  {' '}vs {prevMonthShortLabel} ({fmtHours(prevMonthTotal)})
                </>
              ) : (
                <span>{monthLabel}</span>
              )}
            </div>
          </div>
          <div className="sum-card">
            <div className="sum-lbl">R&D Hours</div>
            <div className="sum-val">{fmtHours(rdHoursWeek)}</div>
            <div className="sum-sub">{monthShortLabel} MTD: {fmtHours(rdMonthlyTotal)}</div>
          </div>
          <div className="sum-card">
            <div className="sum-lbl">G&A Hours</div>
            <div className="sum-val">{fmtHours(gaHoursWeek)}</div>
            {bhHoursWeek > 0
              ? <div className="sum-sub">+ {fmtHours(bhHoursWeek)} Bank Holiday</div>
              : <div className="sum-sub">{monthLabel}</div>}
          </div>
          <div className="sum-card">
            <div className="sum-lbl">YTD Total</div>
            <div className="sum-val">{ytdTotal !== null ? fmtHours(ytdTotal) : '—'}</div>
            <div className="sum-sub">{parsePeriodMonth(periodMonth).year} year to date</div>
          </div>
          <div
            className={statusCardCls}
            title={currentStatus === TimesheetStatus.Rejected && rejectionNote ? `Reason: ${rejectionNote}` : undefined}
          >
            <div className="sum-lbl">Status</div>
            <div className="sum-val" style={{ fontSize: 16, letterSpacing: 0, marginTop: 4 }}>
              {STATUS_LABELS[currentStatus]}
            </div>
            <div className="sum-sub" style={currentStatus === TimesheetStatus.Rejected && rejectionNote ? { color: 'var(--red)', whiteSpace: 'normal', wordBreak: 'break-word' } : undefined}>
              {isDirty ? 'Unsaved changes'
                : currentStatus === TimesheetStatus.Rejected && rejectionNote
                  ? rejectionNote
                  : currentStatus === TimesheetStatus.Draft ? 'Not submitted'
                  : ''}
            </div>
          </div>
        </div>

        {/* Timesheet card */}
        <div className="ts-card">
          <div className="ts-card-hdr">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="ts-card-title">
                {isOwnSheet
                  ? <>Hello {userName.split(' ')[0]} 👋&nbsp;&nbsp;Weekly Timesheet</>
                  : <>{viewedPerson.fullName}&apos;s Weekly Timesheet</>}
              </span>
              <div className='wk-nav' style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToPrevWeek}
                  disabled={isFirstWeekOfMonth}
                  title="Previous week"
                  style={{ padding: '2px 7px' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <span className='wk-lbl-top'>
                  {weekLabel}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextWeek}
                  disabled={isLastWeekOfMonth}
                  title="Next week"
                  style={{ padding: '2px 7px' }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
            <span
              className={PILL_CLASS[currentStatus]}
              title={currentStatus === TimesheetStatus.Rejected && rejectionNote ? `Reason: ${rejectionNote}` : undefined}
            >
              {STATUS_LABELS[currentStatus]}
            </span>
          </div>

          {/* Viewing someone else's timesheet banner */}
          {!isOwnSheet && (
            <div className="month-rule-note" style={{ margin: '12px 18px 0' }}>
              <svg className="mrn-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <strong>Read-only view.</strong> You are viewing {viewedPerson.fullName}&apos;s timesheet
                {canActAsSuperApprover ? ' as a super approver' : ' as their line manager'} — edits and submissions are disabled here.
              </div>
            </div>
          )}

          {/* Strict calendar month banner */}
          <div className="month-rule-note" style={{ margin: '12px 18px 0' }}>
            <svg className="mrn-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div>
              <strong>Strict calendar months.</strong> Timesheets are always scoped to a single calendar month.
              When a week spans a month boundary, days outside the current month are greyed out and read-only —
              enter those hours on the adjacent month&apos;s timesheet.
            </div>
          </div>

          {/* Rejection reason callout */}
          {monthStatus === TimesheetStatus.Rejected && rejectionNote && (
            <div
              className="month-rule-note"
              style={{ margin: '12px 18px 0', background: 'var(--red-soft)', border: '1px solid rgba(220,38,38,0.25)', borderLeft: '3px solid var(--red)' }}
            >
              <svg className="mrn-ico" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <div>
                <strong style={{ color: 'var(--red)' }}>Rejected.</strong> {rejectionNote}
              </div>
            </div>
          )}

          {/* Validation / save errors */}
          {submitErrors.length > 0 && (
            <div className="err-banner" style={{ margin: '12px 18px 0' }}>
              <div>{submitErrors.map((e, i) => <div key={i}>{e}</div>)}</div>
            </div>
          )}
          {saveError && (
            <div className="err-banner" style={{ margin: '12px 18px 0' }}>
              Save failed: {saveError.message}
            </div>
          )}

          {/* Grid */}
          <div style={{ overflowX: 'auto' }}>
            <table className="ts-tbl" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th className="tcol">Project / Task</th>
                  {weekDays.map((d, i) => {
                    const oom  = !isDateInMonth(d, periodMonth);
                    const wknd = isWeekend(parseISODate(d));
                    const bh   = isDayBankHoliday(d);
                    return (
                      <th key={d} className={oom ? 'col-oom' : wknd ? 'wknd-hdr' : ''}>
                        {DAY_SHORT[i]}{bh && <span className="bh-dot" />}
                        <br />
                        <small style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>
                          {formatDayShort(d)}
                        </small>
                      </th>
                    );
                  })}
                  <th className="ttotal">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <ProjectRowComponent
                    key={row.projectCode}
                    row={row}
                    isFirst={idx === 0}
                    weekDays={weekDays}
                    onCellChange={handleCellChange}
                    onCellBlur={undefined}
                    onRemove={isReadOnlySheet ? undefined : handleRemoveRow}
                    onFridayEnter={!isReadOnlySheet && availableProjects.length > 0
                      ? () => setAddedCodes(prev => [...prev, ''])
                      : undefined}
                  />
                ))}
                {bhRow && (
                  <BankHolidayRowComponent
                    row={bhRow}
                    isReadOnlySheet={isReadOnlySheet}
                    onToggleOverride={!isReadOnlySheet ? handleToggleBHOverride : undefined}
                  />
                )}
                {addedCodes.map((code, rowIdx) => {
                  if (code) return null;
                  return (
                    <tr key={`pending-${rowIdx}`}>
                      <td className="tcell" colSpan={9}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <select
                            className="finput"
                            style={{ maxWidth: 320, fontSize: 13 }}
                            value=""
                            onChange={e => {
                              const selected = e.target.value;
                              if (!selected) return;
                              setAddedCodes(prev => {
                                const next = prev.map((c, i) => (i === rowIdx ? selected : c));
                                monthAddedCodes.current.set(calendarMonth, next.filter(Boolean));
                                return next;
                              });
                            }}
                          >
                            <option value="">Select project…</option>
                            {availableProjects.map(p => (
                              <option key={p.projectCode} value={p.projectCode}>
                                {p.projectCode} — {p.projectName}{p.status !== ProjectStatus.Active ? ' (Expired)' : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: 12, padding: '2px 8px' }}
                            onClick={() => setAddedCodes(prev => prev.filter((_, i) => i !== rowIdx))}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!isReadOnlySheet && (
                  <tr className="add-row">
                    <td colSpan={9}>
                      <button
                        className="add-row-btn"
                        disabled={availableProjects.length === 0}
                        onClick={() => setAddedCodes(prev => [...prev, ''])}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add project or task...
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="tfoot-row">
                  <td className="tfoot-lbl">Daily Total</td>
                  {weekDays.map(d => {
                    const oom  = !isDateInMonth(d, periodMonth);
                    const wknd = isWeekend(parseISODate(d));
                    const bh   = isDayBankHoliday(d);
                    const bhOverridden = bh && overriddenBHDates.has(d);
                    const raw  = dailyTotals.get(d) ?? 0;
                    const bhRemaining = bhOverridden ? (bhRow?.cells.find(c => c.date === d)?.hours ?? 0) : 0;
                    const displayTotal = raw + bhRemaining;
                    const over = !bh && isDayOverThreshold(d);
                    return (
                      <td
                        key={d}
                        className={`tfoot-day${oom ? ' col-oom' : wknd ? ' col-wknd' : ''}`}
                        style={{ color: (bh && !bhOverridden && displayTotal === 0) ? 'var(--amber)' : over ? 'var(--red)' : undefined }}
                      >
                        {bh && !bhOverridden && displayTotal === 0
                          ? `${fmtHours(VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS)} bh`
                          : (oom || displayTotal === 0) ? '–' : decimalToHHMM(displayTotal)}
                      </td>
                    );
                  })}
                  <td className="tfoot-grand">
                    {grandTotal > 0 ? fmtHours(grandTotal) : '–'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Actions bar */}
          <div className="ts-actions">
            <div className="ts-actions-l">
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                {weekLabel} — {STATUS_LABELS[currentStatus].toLowerCase()}
                {isDirty ? ' (unsaved)' : ''}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={handleExport}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export
              </button>
            </div>
            <div className="ts-actions-r">
              {savedLabel && <span className="saved-lbl">{savedLabel}</span>}
              {!isReadOnlySheet && (
                <>
                  <button
                    className={`btn btn-sm${isDirty && !(isSaving || showSaving) ? ' btn-save-dirty' : ' btn-ghost'}`}
                    onClick={() => { void handleSaveDraft(); }}
                    disabled={isSaving || showSaving}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    {isSaving || showSaving ? 'Saving…' : canActAsSuperApprover ? 'Save' : 'Save Draft'}
                  </button>
                  {(monthStatus === TimesheetStatus.Draft || monthStatus === TimesheetStatus.Rejected) && (
                    <button
                      className="btn btn-success"
                      onClick={() => { void handleSubmitRequest(); }}
                      disabled={isSaving || isSubmitting}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {submitLabel}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating save reminder */}
      {isDirty && !isReadOnlySheet && (
        <button className="save-reminder" onClick={() => { void handleSaveDraft(); }}>
          💾 Please press save!
        </button>
      )}

      {/* Remove row confirmation */}
      {pendingRemoveCode && (
        <div className="modal-backdrop" onClick={() => setPendingRemoveCode(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <p className="modal-title">Remove project row?</p>
            <p className="modal-subtitle">
              This will permanently delete all timesheet hour entries for <strong>{pendingRemoveCode}</strong> across the entire month of <strong>{monthDisplayLabel}</strong> from SharePoint. This cannot be undone.
            </p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPendingRemoveCode(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => confirmRemoveRow(pendingRemoveCode)}
              >
                Delete &amp; Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore bank holiday confirmation */}
      {pendingBHRestoreDate && (() => {
        const workedEntries = entries.filter(
          e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.dateWorked === pendingBHRestoreDate && e.hours > 0
        );
        const totalWorked = workedEntries.reduce((s, e) => s + e.hours, 0);
        return (
          <div className="modal-backdrop" onClick={() => setPendingBHRestoreDate(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <p className="modal-title">Restore bank holiday?</p>
              <p className="modal-subtitle">
                You have logged <strong>{fmtHours(totalWorked)}</strong> of project hours on this bank holiday
                ({workedEntries.map(e => e.projectCode).join(', ')}).
                Restoring will delete those entries and reinstate the full <strong>{fmtHours(VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS)}</strong> bank holiday allowance.
              </p>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setPendingBHRestoreDate(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => confirmBHRestore(pendingBHRestoreDate)}
                >
                  Delete &amp; Restore
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Submit confirmation */}
      {showSubmitConfirm && (
        <div className="modal-backdrop" onClick={() => setShowSubmitConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <p className="modal-title">{isLastWeekOfMonth ? 'Submit for Month?' : 'Submit for Approval?'}</p>
            <p className="modal-subtitle">
              {isLastWeekOfMonth
                ? "You're on the last week of the month. Submitting will send the full month's timesheet for manager approval."
                : "Are you sure you want to submit this month's timesheet? Once submitted it will be sent for manager approval."}
            </p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowSubmitConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={() => { void handleSubmitConfirm(); }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation */}
      {showCancelConfirm && (
        <div className="modal-backdrop" onClick={() => setShowCancelConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <p className="modal-title">Discard changes?</p>
            <p className="modal-subtitle">
              You have unsaved entries. They will be lost if you cancel.
            </p>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowCancelConfirm(false)}>
                Keep editing
              </button>
              <button
                className="btn btn-danger"
                onClick={() => { discardChanges(); setAddedCodes(prev => prev.filter(Boolean)); setShowCancelConfirm(false); }}
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── ProjectRowComponent ───────────────────────────────────────────────────────

interface ProjectRowProps {
  row: GridRow;
  isFirst: boolean;
  weekDays: readonly ISODateString[];
  onCellChange: (code: string, date: ISODateString, value: string) => void;
  onCellBlur?: () => void;
  onRemove?: (code: string) => void;
  onFridayEnter?: () => void;
}

function ProjectRowComponent({ row, isFirst, onCellChange, onCellBlur, onRemove, onFridayEnter }: ProjectRowProps) {
  const canRemove = onRemove && row.rowTotal === 0;
  return (
    <tr className={isFirst ? 'pin-row' : ''}>
      <td className="tcell">
        <div className="task-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{row.projectCode}&nbsp;<TypeBadge type={row.projectType} /></span>
          {canRemove && (
            <button
              type="button"
              className="row-remove-btn"
              title="Remove project row"
              aria-label={`Remove ${row.projectCode}`}
              onClick={() => onRemove!(row.projectCode)}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--text-dim)', fontSize: 14, lineHeight: 1,
                padding: '0 4px',
              }}
            >
              ✕
            </button>
          )}
        </div>
        {row.projectName !== row.projectCode && (
          <div className="task-meta">{row.projectName}</div>
        )}
      </td>
      {row.cells.map(cell => (
        <ProjectCell
          key={cell.date}
          cell={cell}
          row={row}
          onCellChange={onCellChange}
          onCellBlur={onCellBlur}
          onFridayEnter={parseISODate(cell.date).getDay() === 5 ? onFridayEnter : undefined}
        />
      ))}
      <td className="total-cell">
        {row.rowTotal > 0 ? fmtHours(row.rowTotal) : '–'}
      </td>
    </tr>
  );
}

// ── ProjectCell ───────────────────────────────────────────────────────────────

interface ProjectCellProps {
  cell: CellData;
  row: GridRow;
  onCellChange: (code: string, date: ISODateString, value: string) => void;
  onCellBlur?: () => void;
  onFridayEnter?: () => void;
}

function ProjectCell({ cell, row, onCellChange, onCellBlur, onFridayEnter }: ProjectCellProps) {
  const [rawVal, setRawVal] = useState<string | null>(null);

  const isEmpty = rawVal !== null ? rawVal === '' : cell.hours === null;
  const cls = [
    'hr-input',
    cell.isBH && !cell.isOverriddenBH ? 'bh'   : '',
    cell.isOOM ? 'oom'  : '',
    cell.isWeekend && !cell.isBH && !cell.isOOM ? 'wknd' : '',
    isEmpty ? 'empty' : '',
  ].filter(Boolean).join(' ');

  const title = cell.isOOM ? "Enter these hours on the adjacent month's timesheet"
    : cell.isBH             ? 'Bank holiday — hours entered here will reduce the BH allowance'
    : undefined;

  const tdCls = cell.isOOM ? 'col-oom' : cell.isWeekend ? 'col-wknd' : '';
  const committedDisplay = cell.hours === null ? '' : decimalToHHMM(cell.hours);
  const displayVal = rawVal !== null ? rawVal : committedDisplay;

  const doStep = (dir: 1 | -1) => {
    const cur = rawVal !== null ? rawVal : committedDisplay;
    const base = cur === '' ? 0 : (isNaN(hhmmToDecimal(cur)) ? 0 : hhmmToDecimal(cur));
    const next = dir === 1
      ? Math.min(VALIDATION.MAX_DAILY_HOURS, base + 0.25)
      : Math.max(0, base - 0.25);
    const hhmm = next === 0 ? '' : decimalToHHMM(next);
    setRawVal(hhmm);
    onCellChange(row.projectCode, cell.date, hhmm);
  };

  return (
    <td className={tdCls}>
      <div className="hr-input-wrap">
        <input
          className={cls}
          type="text"
          inputMode="decimal"
          value={displayVal}
          placeholder="–"
          readOnly={cell.isReadOnly}
          title={title}
          onChange={cell.isReadOnly ? undefined : e => setRawVal(e.target.value)}
          onKeyDown={cell.isReadOnly ? undefined : e => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
              doStep(e.key === 'ArrowUp' ? 1 : -1);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const el = e.currentTarget as HTMLInputElement;
              const isFriday = parseISODate(cell.date).getDay() === 5;
              if (isFriday) {
                el.blur();
                onFridayEnter?.();
              } else {
                const rowEl = el.closest('tr');
                const siblings = rowEl
                  ? Array.from(rowEl.querySelectorAll<HTMLInputElement>('input.hr-input:not([readonly])'))
                  : [];
                const idx = siblings.indexOf(el);
                if (idx >= 0 && idx < siblings.length - 1) {
                  siblings[idx + 1].focus();
                } else {
                  el.blur();
                }
              }
            }
          }}
          onBlur={cell.isReadOnly ? undefined : () => {
            if (rawVal !== null) onCellChange(row.projectCode, cell.date, rawVal);
            setRawVal(null);
            onCellBlur?.();
          }}
        />
        {!cell.isReadOnly && (
          <div className="hr-spin-btns">
            <button className="hr-spin-btn" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => doStep(1)} />
            <button className="hr-spin-btn" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => doStep(-1)} />
          </div>
        )}
      </div>
    </td>
  );
}

// ── BankHolidayRowComponent ───────────────────────────────────────────────────

interface BankHolidayRowProps {
  row: GridRow;
  isReadOnlySheet: boolean;
  onToggleOverride?: (date: ISODateString) => void;
}

function BankHolidayRowComponent({ row, isReadOnlySheet, onToggleOverride }: BankHolidayRowProps) {
  return (
    <tr style={{ background: 'rgba(217,119,6,0.05)' }}>
      <td className="tcell">
        <div className="task-name" style={{ color: 'var(--amber)' }}>
          🏖 Bank Holiday{' '}
          <span className="badge" style={{ background: 'rgba(217,119,6,0.15)', color: 'var(--amber)' }}>
            Auto
          </span>
        </div>
        <div className="task-meta">{row.bankHolidayName} — auto-inserted</div>
      </td>
      {row.cells.map(cell => (
        <td key={cell.date} style={{ verticalAlign: 'top' }}>
          <input
            className={`hr-input bh${cell.isOverriddenBH ? ' bh-overridden' : ''}`}
            type="text"
            value={cell.hours ? decimalToHHMM(cell.hours) : ''}
            readOnly
            placeholder="–"
            title={(() => {
              const hasWorkedHours = cell.isBH && cell.hours !== null && cell.hours < VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS;
              if (hasWorkedHours) return `Remaining bank holiday: ${cell.hours ? decimalToHHMM(cell.hours) : '0h'} (${fmtHours(VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS)} minus project hours logged)`;
              if (cell.isBH)     return 'Bank Holiday — 7.5h auto-inserted. Click "Worked?" to log project hours instead.';
              if (cell.isWeekend) return 'Weekend';
              return 'Not a bank holiday';
            })()}
            style={(() => {
              const hasWorkedHours = cell.isBH && cell.hours !== null && cell.hours < VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS;
              return hasWorkedHours
                ? { color: 'var(--amber)', fontWeight: 600, opacity: 0.65 }
                : cell.isBH ? { color: 'var(--amber)', fontWeight: 600 }
                : undefined;
            })()}
          />
          {!isReadOnlySheet && cell.isBH && onToggleOverride && (() => {
            // Show "↩ Restore" if the day is overridden (editing mode) OR project hours
            // have already been logged (cell.hours reduced below the full BH allowance)
            const hasWorkedHours = cell.hours !== null && cell.hours < VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS;
            const isRestoreMode  = cell.isOverriddenBH || hasWorkedHours;
            return (
              <button
                type="button"
                onClick={() => onToggleOverride(cell.date)}
                title={isRestoreMode ? 'Restore full bank holiday hours' : 'I worked on this bank holiday'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                  width: '100%',
                  marginTop: 3,
                  padding: '3px 4px',
                  fontSize: 10,
                  fontWeight: isRestoreMode ? 400 : 700,
                  borderRadius: 4,
                  border: isRestoreMode
                    ? '1px solid var(--border)'
                    : '1.5px solid rgba(217,119,6,0.6)',
                  background: isRestoreMode
                    ? 'transparent'
                    : 'rgba(217,119,6,0.13)',
                  color: isRestoreMode ? 'var(--text-muted)' : 'var(--amber)',
                  cursor: 'pointer',
                  letterSpacing: isRestoreMode ? undefined : '0.02em',
                }}
              >
                {isRestoreMode ? <>↩ Restore</> : <>💼 Worked?</>}
              </button>
            );
          })()}
        </td>
      ))}
      <td className="total-cell" style={{ color: 'var(--amber)' }}>
        {row.rowTotal > 0 ? fmtHours(row.rowTotal) : '–'}
      </td>
    </tr>
  );
}
